// ProxyList.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { proxiesAPI } from '../../api/proxies';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProxyCard from '../../components/proxies/ProxyCard';

const ProxiesList = () => {
  const { t } = useTranslation();
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  
  // Реф для хранения предыдущих данных для сравнения
  const prevProxiesMapRef = useRef(new Map());
  // Реф для хранения изменённых полей по каждому proxyId
  const changedFieldsRef = useRef(new Map());

  // Загрузка прокси с умным сравнением
  const loadProxies = useCallback(async (filterParam = filter, isInitial = false) => {
    try {
      if (isInitial || proxies.length === 0) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      
      const params = filterParam !== 'all' ? { status: filterParam } : {};
      const data = await proxiesAPI.getList(params);
      const newProxies = data || [];
      
      // Создаём Map для быстрого доступа по proxyId
      const newProxiesMap = new Map(newProxies.map(p => [p.proxyId, p]));
      const prevMap = prevProxiesMapRef.current;
      const changedFields = new Map();
      
      // Сравниваем только те прокси, которые уже были
      newProxies.forEach(newProxy => {
        const prevProxy = prevMap.get(newProxy.proxyId);
        if (prevProxy) {
          // Сравниваем поля и собираем изменённые
          const fields = ['status', 'countryName', 'regionName', 'cityName', 'lastHeartbeat', 'connectedClients', 'isOnline'];
          const changed = fields.filter(field => prevProxy[field] !== newProxy[field]);
          
          if (changed.length > 0) {
            changedFields.set(newProxy.proxyId, changed);
          }
        }
      });
      
      // Сохраняем изменённые поля для передачи в ProxyCard
      changedFieldsRef.current = changedFields;
      
      // Обновляем состояние
      setProxies(newProxies);
      // Обновляем реф с предыдущими данными
      prevProxiesMapRef.current = newProxiesMap;
      
    } catch (error) {
      console.error('Error loading proxies:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [filter, proxies.length]);

  // Initial load + polling каждые 30 сек
  useEffect(() => {
    loadProxies(filter, true);
    const interval = setInterval(() => loadProxies(filter, false), 30000);
    return () => clearInterval(interval);
  }, [loadProxies]);

  // Сброс подсветки изменений через 2 секунды
  useEffect(() => {
    if (changedFieldsRef.current.size > 0) {
      const timer = setTimeout(() => {
        changedFieldsRef.current.clear();
        // Форсируем минорный ре-рендер для сброса подсветки
        setProxies(prev => [...prev]);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [proxies]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    loadProxies(newFilter, false);
  }, [loadProxies]);

  // Мемоизируем changedFields для текущего рендера
  const getChangedFieldsForProxy = useCallback((proxyId) => {
    return changedFieldsRef.current.get(proxyId) || [];
  }, []);

  // Анимации
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.03, delayChildren: 0.05 }
    }
  };

  // Фильтрация списка (выносим отдельно, чтобы не пересчитывать при каждом рендере)
  const filteredProxies = useMemo(() => {
    if (filter === 'all') return proxies;
    if (filter === 'online') return proxies.filter(p => p.isOnline || p.status === 'online');
    return proxies.filter(p => !p.isOnline && p.status !== 'online');
  }, [proxies, filter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('proxies.title')}</h1>
          <p className="mt-2 text-gray-600">{t('proxies.subtitle')}</p>
        </div>
        
        {/* Индикатор обновления */}
        {isRefreshing && !loading && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex items-center text-sm text-blue-600"
          >
            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            {t('common.refreshing')}
          </motion.div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {['all', 'online', 'offline'].map((filterValue) => (
          <button
            key={filterValue}
            onClick={() => handleFilterChange(filterValue)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === filterValue
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t(`proxies.filters.${filterValue}`)}
          </button>
        ))}
      </div>

      {/* Proxies Grid */}
      {loading && proxies.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredProxies.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
            <p className="mt-4 text-sm text-gray-600">
              {filter === 'all' 
                ? t('proxies.empty.all') 
                : t('proxies.empty.filtered', { status: t(`proxies.status.${filter}`) })}
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* Grid контейнер */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* AnimatePresence с mode="popLayout" для плавного добавления/удаления */}
            <AnimatePresence mode="popLayout">
              {filteredProxies.map((proxy) => (
                <ProxyCard
                  key={proxy.proxyId} // ✅ Стабильный ключ по ID — критично для анимаций
                  proxy={proxy}
                  changedFields={getChangedFieldsForProxy(proxy.proxyId)} // ✅ Массив строк с изменёнными полями
                />
              ))}
            </AnimatePresence>
          </motion.div>
          
          {/* Footer stats */}
          <div className="flex justify-end text-xs text-gray-500">
            <span>{t('proxies.total')}: {filteredProxies.length}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(ProxiesList);